<script setup lang="ts">
import { AppSwiperGallery } from '@libs/ui';

import type { HotelSelectionCardData } from '../models';
import { useCoreStore, useStatsigEventsStore } from '../../../core/stores';
import {
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';
import { usePreviouslyUserSessionStore } from '../../../core/stores/previouslyUserSession/previouslyUserSessionStore';

import HotelSelectionCardContent from './HotelSelectionCardContent.vue';
import HotelSelectionCardLayout from './HotelSelectionCardLayout.vue';
import HotelSelectionCardSummary from './HotelSelectionCardSummary.vue';

interface HotelSelectionCardProps {
  data: HotelSelectionCardData;
}

const props = defineProps<HotelSelectionCardProps>();

interface HotelSelectionCardEmits {
  (eventName: 'hightlight-on-map', hotelCode: string | undefined): void;
}

const emit = defineEmits<HotelSelectionCardEmits>();

const coreStore = useCoreStore();
const hotelSelectionStore = useHotelSelectionStore();
const statsigEventsStore = useStatsigEventsStore();
const previouslyUserSessionStore = usePreviouslyUserSessionStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();

function onHotelSelectionCardClicked(hasAvailableGuestRating: boolean): void {
  const isPreviouslySeenHotel =
    previouslyUserSessionStore.previouslySeenHotelCodes[
      props.data.context.hotelCode
    ];
  statsigEventsStore.logHotelSelectionListCardClicked({
    isPreviouslySeenHotel,
  });
  if (hasAvailableGuestRating) {
    statsigEventsStore.logHotelCardClickedWithRating();
  }
  hotelSelectionStore.openHotelDetails(props.data);
}

function onMouseEnter(): void {
  emit('hightlight-on-map', props.data.context.hotelCode);
}

function onMouseLeave(): void {
  emit('hightlight-on-map', undefined);
}

function onGalleryImageSwipe(): void {
  statsigEventsStore.logHotelSelectionGalleryImageSwipe();
}

function onPeekPrice(): void {
  statsigEventsStore.logHotelSelectionPeekPriceModalOpened();
  hotelSelectionModalsStore.openPeekPriceModal(
    props.data.context.ingredientIndex,
    props.data.context.hotelCode
  );
}
</script>

<template>
  <div class="hotel-selection-card-wrapper">
    <HotelSelectionCardLayout
      class="hotel-selection-card"
      :card-state="props.data.context.selectionState"
      :is-previously-seen="props.data.context.isPreviouslySeen"
      @click="
        onHotelSelectionCardClicked(
          !!props.data.hotelSelectionCardContent.guestRating
        )
      "
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @focus="onMouseEnter"
      @blur="onMouseLeave"
    >
      <template #image>
        <AppSwiperGallery
          :image-urls="props.data.imageUrls"
          :show-nav-buttons="true"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="coreStore.handleImageLoadError"
          slides-amount-position="right"
          slides-amount-size="s"
          @gallery-image-swipe="onGalleryImageSwipe"
        />
      </template>
      <template #content>
        <HotelSelectionCardContent
          :data="props.data.hotelSelectionCardContent"
          :context="props.data.context"
          class="content-wrapper"
        />
      </template>
      <template #summary>
        <HotelSelectionCardSummary
          :data="props.data.hotelSelectionCardSummary"
          :context="props.data.context"
          @peek-price="onPeekPrice"
        />
      </template>
    </HotelSelectionCardLayout>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-wrapper {
  .hotel-selection-card {
    cursor: pointer;

    @include on-hover {
      :deep(.hotel-selection-card-wrapper) {
        background: var(--color-secondary-97);
        transition: background var(--transition-short);
      }
    }
  }
}
</style>
